<template>
  <div class="pending-changes-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Oczekujące zmiany</h1>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <v-card class="content-card">
          <v-card-title class="text-h5 primary--text font-weight-bold">
            <v-icon left color="primary">mdi-account-edit</v-icon>
            {{ $t('pendingChanges.title') }}
          </v-card-title>

          <v-card-text>
            <!-- Changes Table -->
            <v-data-table
              :headers="headers"
              :items="driverChanges"
              :loading="loading"
              :options.sync="options"
              :server-items-length="totalItems"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100],
                'items-per-page-text': $t('pendingChanges.changesPerPage'),
                'page-text': $t('pendingChanges.pageText')
              }"
              class="elevation-1"
              :loading-text="$t('pendingChanges.loadingText')"
              :no-data-text="$t('pendingChanges.noDataText')"
            >
              <!-- Driver Info -->
              <template v-slot:[`item.driver`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="40" class="mr-3">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">
                      {{ item.firstName }} {{ item.lastName }}
                    </div>
                    <div class="text-caption grey--text">
                      ID: {{ item.id }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Changed Fields -->
              <template v-slot:[`item.changedFields`]="{ item }">
                <div>
                  <v-chip
                    v-for="field in getChangedFields(item.changes)"
                    :key="field"
                    small
                    class="mr-1 mb-1"
                    color="info"
                  >
                    {{ getFieldLabel(field) }}
                  </v-chip>
                </div>
              </template>

              <!-- Status -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.changesApproved)"
                  text-color="white"
                >
                  {{ getStatusText(item.changesApproved) }}
                </v-chip>
              </template>

              <!-- Created Date — data zgłoszenia zmiany (requestedAt) -->
              <template v-slot:[`item.createdAt`]="{ item }">
                <div>
                  <div>{{ formatDate(item.changes && item.changes.requestedAt ? item.changes.requestedAt : item.updatedAt) }}</div>
                  <div class="text-caption grey--text">
                    {{ formatTimeAgo(item.changes && item.changes.requestedAt ? item.changes.requestedAt : item.updatedAt) }}
                  </div>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  small
                  color="info"
                  @click="viewChanges(item)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  {{ $t('pendingChanges.preview') }}
                </v-btn>

                <v-btn
                  small
                  color="success"
                  @click="approveChanges(item)"
                  :loading="processingChanges.includes(item.id)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-check</v-icon>
                  {{ $t('pendingChanges.approve') }}
                </v-btn>

                <v-btn
                  small
                  color="error"
                  @click="rejectChanges(item)"
                  :loading="processingChanges.includes(item.id)"
                >
                  <v-icon left small>mdi-close</v-icon>
                  {{ $t('pendingChanges.reject') }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Confirmation Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          {{ confirmAction.title }}
        </v-card-title>
        <v-card-text>
          {{ confirmAction.text }}
          <v-textarea
            v-if="confirmAction.action === 'reject'"
            v-model="rejectionReason"
            :label="$t('pendingChanges.rejectionReasonOptional')"
            rows="3"
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            :color="confirmAction.color"
            @click="executeConfirmedAction"
            :loading="confirmAction.loading"
          >
            {{ confirmAction.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Changes Preview Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="changesDialog" max-width="800">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left>mdi-account-edit</v-icon>
          {{ $t('pendingChanges.changesPreview') }}
        </v-card-title>
        <v-card-text v-if="selectedChange">
          <div class="mb-4">
            <div class="text-h6 mb-2">
              {{ $t('pendingChanges.driverLabel') }}: {{ selectedChange.firstName }} {{ selectedChange.lastName }}
            </div>
            <div class="text-caption grey--text mb-4">
              {{ $t('pendingChanges.reportDate') }}: {{ formatDate(selectedChange.changes.requestedAt || selectedChange.createdAt) }}
            </div>
          </div>

          <!-- Changes Comparison -->
          <v-row>
            <v-col cols="6">
              <v-card outlined>
                <v-card-title class="text-subtitle-1 red--text">
                  <v-icon left color="red">mdi-minus</v-icon>
                  {{ $t('pendingChanges.currentValues') }}
                </v-card-title>
                <v-card-text>
                  <div v-for="(value, field) in currentDriverData" :key="'current-' + field" class="mb-2">
                    <strong>{{ getFieldLabel(field) }}:</strong><br>
                    <span class="text-body-2">{{ formatFieldValue(field, value) }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card outlined>
                <v-card-title class="text-subtitle-1 green--text">
                  <v-icon left color="green">mdi-plus</v-icon>
                  {{ $t('pendingChanges.newValues') }}
                </v-card-title>
                <v-card-text>
                  <div v-for="field in getChangedFields(selectedChange.changes)" :key="'new-' + field" class="mb-2">
                    <strong>{{ getFieldLabel(field) }}:</strong><br>
                    <span class="text-body-2">{{ formatFieldValue(field, selectedChange.changes[field]) }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-alert
            v-if="hasZusChange(selectedChange)"
            dense
            text
            type="info"
            class="mt-4 mb-0"
          >
            Typ ZUS po zatwierdzeniu wejdzie dopiero od następnego tygodnia
            rozliczeniowego (poniedziałek {{ nextSettlementWeekLabel() }}) —
            bieżący tydzień rozliczy się po staremu.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="changesDialog = false">
            {{ $t('common.close') }}
          </v-btn>
          <v-btn
            v-if="selectedChange"
            color="success"
            @click="approveChanges(selectedChange)"
            :loading="processingChanges.includes(selectedChange.id)"
            class="mr-2"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ $t('pendingChanges.approve') }}
          </v-btn>
          <v-btn
            v-if="selectedChange"
            color="error"
            @click="rejectChanges(selectedChange)"
            :loading="processingChanges.includes(selectedChange.id)"
          >
            <v-icon left small>mdi-close</v-icon>
            {{ $t('pendingChanges.reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'PendingDriversChangesPage',
  data() {
    return {
      loading: false,
      driverChanges: [],
      currentDriverData: {},
      totalItems: 0,

      options: {
        page: 1,
        itemsPerPage: 25,
      },

      headers: [
        {
          text: 'Kierowca',
          value: 'driver',
          sortable: false,
          width: '200px'
        },
        {
          text: 'Zmienione pola',
          value: 'changedFields',
          sortable: false,
          width: '250px'
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '120px'
        },
        {
          text: 'Data zgłoszenia',
          value: 'createdAt',
          sortable: true,
          width: '180px'
        },
        {
          text: 'Akcje',
          value: 'actions',
          sortable: false,
          width: '250px'
        }
      ],

      processingChanges: [],

      confirmDialog: false,
      confirmAction: {
        title: '',
        text: '',
        color: '',
        confirmText: '',
        loading: false,
        action: null,
        item: null
      },
      rejectionReason: '',

      changesDialog: false,
      selectedChange: null,

      fieldLabels: {
        firstName: 'Imię',
        lastName: 'Nazwisko',
        phone: 'Telefon',
        email: 'Email',
        password: 'Hasło',
        streetAddress: 'Ulica',
        homeNumber: 'Numer domu',
        aptNumber: 'Numer mieszkania',
        postalCode: 'Kod pocztowy',
        city: 'Miasto',
        accountNo: 'Numer konta',
        isAbroadBankAccount: 'Konto za granicą',
        cashPayout: 'Wypłata gotówkowa',
        payoutSchedule: 'Częstotliwość wypłat',
        zusType: 'Typ ZUS'
      },
      payoutScheduleLabels: {
        DAILY: 'Codziennie',
        WEEKLY: 'Co tydzień',
        EVERY_2_WEEKS: 'Co 2 tygodnie',
        MONTH: 'Miesięcznie'
      },
      zusTypeLabels: {
        B2B: 'B2B',
        CONNECTION: 'Zbieg tytułów',
        NOT_HIRED_L4: 'Nie zatrudniony z L4',
        NOT_HIRED_NO_L4: 'Nie zatrudniony bez L4',
        HIRED_BELOW_WITH_L4: 'Zatrudniony poniżej z L4',
        HIRED_BELOW_NO_L4: 'Zatrudniony poniżej bez L4',
        HIRED_ABOVE: 'Zatrudniony powyżej',
        STUDENT: 'Student',
        UNDER_26_NO_L4: 'Poniżej 26 lat bez L4',
        UNDER_26_L4: 'Poniżej 26 lat z L4'
      }
    };
  },

  watch: {
    options: {
      handler() {
        this.fetchDriverChanges();
      },
      deep: true
    }
  },

  methods: {
    async fetchDriverChanges() {
      this.loading = true;
      try {
        const calculatedSkip = 1 + (this.options.page - 1) * this.options.itemsPerPage;

        const params = {
          take: this.options.itemsPerPage,
          skip: calculatedSkip,
        };

        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'drivers/pending-changes',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          params
        });

        const allChanges = response.data?.data?.result || response.data || [];

        // Debug logging
        console.log('API Response:', response.data);
        console.log('All changes:', allChanges);
        console.log('Changes with approval status:', allChanges.map(c => ({ id: c.id, firstName: c.firstName, changesApproved: c.changesApproved })));

        // Show all changes for now to debug
        this.driverChanges = allChanges;
        this.totalItems = response.data?.data?.count || allChanges.length;

      } catch (error) {
        console.error('Error fetching driver changes:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Nie udało się pobrać zmian kierowców',
          icon: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchCurrentDriverData(driverId) {
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'drivers/' + driverId,
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });

        return response.data?.data.result || response.data || {};
      } catch (error) {
        console.error('Error fetching current driver data:', error);
        return {};
      }
    },

    async viewChanges(changeItem) {
      this.selectedChange = changeItem;

      // Extract current driver data from the item itself (no need for additional API call)
      const changedFields = this.getChangedFields(changeItem.changes);
      this.currentDriverData = Object.fromEntries(
        changedFields.map(key => [key, changeItem[key]])
      );

      this.changesDialog = true;
    },

    approveChanges(changeItem) {
      this.confirmAction = {
        title: 'Zatwierdź zmiany',
        text: `Czy na pewno chcesz zatwierdzić zmiany dla kierowcy ${changeItem.firstName} ${changeItem.lastName}?`,
        color: 'success',
        confirmText: 'Zatwierdź',
        loading: false,
        action: 'approve',
        item: changeItem
      };
      this.rejectionReason = '';
      this.confirmDialog = true;
    },

    rejectChanges(changeItem) {
      this.confirmAction = {
        title: 'Odrzuć zmiany',
        text: `Czy na pewno chcesz odrzucić zmiany dla kierowcy ${changeItem.firstName} ${changeItem.lastName}?`,
        color: 'error',
        confirmText: 'Odrzuć',
        loading: false,
        action: 'reject',
        item: changeItem
      };
      this.rejectionReason = '';
      this.confirmDialog = true;
    },

    async executeConfirmedAction() {
      this.confirmAction.loading = true;

      try {
        if (this.confirmAction.action === 'approve') {
          await this.performApproveChanges(this.confirmAction.item);
        } else if (this.confirmAction.action === 'reject') {
          await this.performRejectChanges(this.confirmAction.item);
        }

        this.confirmDialog = false;
        this.changesDialog = false;
        this.fetchDriverChanges(); // Refresh the list

      } catch (error) {
        console.error('Error executing action:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd podczas wykonywania operacji',
          icon: 'error'
        });
      } finally {
        this.confirmAction.loading = false;
      }
    },

    async performApproveChanges(changeItem) {
      this.processingChanges.push(changeItem.id);

      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'drivers/approve-changes',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          data: {
            driverId: changeItem.id,
            approved: true
          }
        });

        Swal.fire({
          title: 'Sukces!',
          text: 'Zmiany zostały zatwierdzone',
          icon: 'success',
          timer: 2000
        });

      } finally {
        this.processingChanges = this.processingChanges.filter(id => id !== changeItem.id);
      }
    },

    async performRejectChanges(changeItem) {
      this.processingChanges.push(changeItem.id);

      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'drivers/approve-changes',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          data: {
            driverId: changeItem.id,
            approved: false,
            reason: this.rejectionReason || undefined
          }
        });

        Swal.fire({
          title: 'Odrzucono!',
          text: 'Zmiany zostały odrzucone',
          icon: 'success',
          timer: 2000
        });

      } finally {
        this.processingChanges = this.processingChanges.filter(id => id !== changeItem.id);
      }
    },

    getChangedFields(changes) {
      // Pomiń metadane (requestedAt) oraz pola, które nic nie zmieniają
      // (passwordChanged=false, null/undefined)
      const META_KEYS = new Set(['requestedAt']);
      return Object.entries(changes || {})
        .filter(([key, val]) => {
          if (META_KEYS.has(key)) return false;
          if (key === 'passwordChanged' && !val) return false;
          if (val === null || val === undefined) return false;
          return true;
        })
        .map(([key]) => key);
    },

    getFieldLabel(field) {
      return this.fieldLabels[field] || field;
    },

    formatFieldValue(field, value) {
      if (field === 'isAbroadBankAccount' || field === 'cashPayout') {
        return value ? 'Tak' : 'Nie';
      }
      if (field === 'password') {
        return value ? '••••••••' : 'Brak zmiany';
      }
      if (field === 'payoutSchedule') {
        return this.payoutScheduleLabels[value] || value || 'Brak danych';
      }
      if (field === 'zusType') {
        return this.zusTypeLabels[value] || value || 'Brak danych';
      }
      return value || 'Brak danych';
    },

    // Zmiana ZUS nie wchodzi w dniu akceptacji — planuje się na najbliższy
    // poniedziałek (aplikuje ją import), więc admin musi to widzieć w podglądzie.
    hasZusChange(changeItem) {
      return !!(changeItem && changeItem.changes && changeItem.changes.zusType);
    },

    nextSettlementWeekLabel() {
      const now = new Date();
      const dow = (now.getDay() + 6) % 7; // pon=0
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow + 7);
      return monday.toLocaleDateString('pl-PL');
    },

    // Ta lista zawiera WYŁĄCZNIE oczekujące zgłoszenia (changesApproved=false,
    // changes!=null). Zaakceptowane i odrzucone znikają z listy (backend ustawia
    // changesApproved=true, changes=null). Dlatego false = NOWE zgłoszenie, nie
    // "odrzucone" — poprzednio mylnie pokazywało "Odrzucone".
    getStatusColor(changesApproved) {
      if (changesApproved === true) return 'green';
      if (changesApproved === false) return 'blue';
      if (changesApproved === null) return 'orange';
      return 'grey';
    },

    getStatusText(changesApproved) {
      if (changesApproved === true) return 'Zatwierdzone';
      if (changesApproved === false) return 'Nowe';
      if (changesApproved === null) return 'Oczekujące';
      return 'Nieznany';
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatTimeAgo(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));

      if (diffDays <= 0) {
        // Dziś — pokaż relatywny czas
        const diffMin = Math.floor((now - date) / 60000);
        if (diffMin < 1) return 'przed chwilą';
        if (diffMin < 60) return `${diffMin} min temu`;
        const diffH = Math.floor(diffMin / 60);
        return `${diffH} godz. temu`;
      }
      if (diffDays === 1) return 'wczoraj';
      if (diffDays < 7) return `${diffDays} dni temu`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} tyg. temu`;
      return `${Math.floor(diffDays / 30)} mies. temu`;
    }
  },

  mounted() {
    this.fetchDriverChanges();
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-body-2 {
  word-break: break-word;
}

.pending-changes-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px !important;
  border: 1px solid #2A3142 !important;
}
.pending-changes-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.pending-changes-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.pending-changes-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.pending-changes-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.pending-changes-page >>> .v-card__title {
  color: #E6EAF2 !important;
}
.pending-changes-page >>> .grey--text {
  color: #94A0B8 !important;
}
.pending-changes-page >>> .primary--text {
  color: #5478FF !important;
}
.pending-changes-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.pending-changes-page >>> .v-text-field input,
.pending-changes-page >>> .v-text-field textarea {
  color: #E6EAF2 !important;
}
.pending-changes-page >>> .v-label {
  color: #94A0B8 !important;
}
</style>